/**
 * WebAuthn helpers: convert the server's JSON options into browser objects and the
 * browser's credential back into JSON (base64url fields).
 */

export const b64uToBuffer = (value) => {
  const base64 = String(value).replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
  return Uint8Array.from(window.atob(padded), (c) => c.charCodeAt(0)).buffer;
};

export const bufferToB64u = (buffer) => {
  let binary = "";
  new Uint8Array(buffer).forEach((b) => (binary += String.fromCharCode(b)));
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

export const webAuthnSupported = () => typeof window !== "undefined" && typeof window.PublicKeyCredential === "function";

/** navigator.credentials.get for authentication options from the server */
export async function getAssertion(options) {
  const credential = await navigator.credentials.get({
    publicKey: {
      challenge: b64uToBuffer(options.challenge),
      allowCredentials: (options.allowCredentials || []).map((c) => ({ ...c, id: b64uToBuffer(c.id) })),
      userVerification: options.userVerification || "required",
      rpId: options.rpId,
      timeout: options.timeout,
    },
  });
  if (!credential) throw new Error("No credential returned");
  const { response } = credential;
  return {
    id: credential.id,
    rawId: bufferToB64u(credential.rawId),
    type: credential.type,
    response: {
      authenticatorData: bufferToB64u(response.authenticatorData),
      clientDataJSON: bufferToB64u(response.clientDataJSON),
      signature: bufferToB64u(response.signature),
      ...(response.userHandle ? { userHandle: new TextDecoder("utf-8").decode(response.userHandle) } : {}),
    },
  };
}

/** navigator.credentials.create for registration options from the server */
export async function createCredential(options) {
  const credential = await navigator.credentials.create({
    publicKey: {
      ...options,
      challenge: b64uToBuffer(options.challenge),
      user: { ...options.user, id: new TextEncoder().encode(options.user.id) },
      excludeCredentials: (options.excludeCredentials || [])
        .map((c) => {
          try {
            return { ...c, id: b64uToBuffer(c.id) };
          } catch (e) {
            return null;
          }
        })
        .filter(Boolean),
    },
  });
  if (!credential) throw new Error("No credential created");
  return {
    id: credential.id,
    rawId: bufferToB64u(credential.rawId),
    type: credential.type,
    response: {
      attestationObject: bufferToB64u(credential.response.attestationObject),
      clientDataJSON: bufferToB64u(credential.response.clientDataJSON),
    },
  };
}

